/**
 * User Admin Store
 *
 * Centralizes user administration data fetching and mutations so Lit components
 * remain presentation-only. Follows the shared signals architecture pattern by
 * exposing a computed state object and explicit action functions.
 */
import { computed, signal } from '@lit-labs/signals';
import { callable, connectFunctionsToEmulator, getFirebaseFunctions, DEFAULT_FUNCTIONS_REGION, } from '@df/firebase/functions';
import { DF_EMULATOR_HOST, DF_EMULATOR_PORTS } from '@df/firebase/emulator-config';
import { getInitializedFirebaseApp, shouldUseEmulatorForService } from './firebase-init.js';
/** Internal signal tracking the user list. */
const usersSignal = signal([]);
/** Internal signal tracking loading status for any user admin request. */
const loadingSignal = signal(false);
/** Internal signal tracking the most recent error message. */
const errorSignal = signal('');
/**
 * Computed state consumed by presentation components.
 * Returns a snapshot of the user list, loading, and error state.
 */
export const userAdminState = computed(() => ({
    users: usersSignal.get(),
    loading: loadingSignal.get(),
    error: errorSignal.get(),
}));
let functionsInstance = null;
function ensureFunctionsInstance() {
    if (functionsInstance) {
        return functionsInstance;
    }
    const app = getInitializedFirebaseApp();
    const functions = getFirebaseFunctions(app, DEFAULT_FUNCTIONS_REGION);
    if (shouldUseEmulatorForService('functions')) {
        connectFunctionsToEmulator(functions, {
            host: DF_EMULATOR_HOST,
            port: DF_EMULATOR_PORTS.functions,
        });
    }
    functionsInstance = functions;
    return functionsInstance;
}
/**
 * Loads users from the Cloud Function.
 * Optionally forwards a search query to the callable function.
 */
export async function loadUsers(searchQuery = '') {
    loadingSignal.set(true);
    errorSignal.set('');
    try {
        const fn = callable(ensureFunctionsInstance(), 'getUserList');
        const result = await fn({ searchQuery });
        usersSignal.set(result.data.users);
    }
    catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load users';
        errorSignal.set(message);
        console.error('[user-admin.store] Failed to load users:', error);
        throw error;
    }
    finally {
        loadingSignal.set(false);
    }
}
/**
 * Updates a user's role via the Cloud Function and reloads the list on success.
 * Legacy function - prefer setUserRoles for multiple role support.
 */
export async function updateUserRole(targetUserId, newRole) {
    loadingSignal.set(true);
    errorSignal.set('');
    try {
        const fn = callable(ensureFunctionsInstance(), 'updateUserRole');
        await fn({ targetUserId, newRole });
        await loadUsers();
    }
    catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to update user role';
        errorSignal.set(message);
        console.error('[user-admin.store] Failed to update user role:', error);
        throw error;
    }
    finally {
        loadingSignal.set(false);
    }
}
/**
 * Sets multiple roles for a user via the Cloud Function and reloads the list on success.
 * Supports many-to-many RBAC with automatic permission union calculation.
 */
export async function setUserRoles(targetUserId, roles) {
    loadingSignal.set(true);
    errorSignal.set('');
    try {
        const fn = callable(ensureFunctionsInstance(), 'setUserRoles');
        await fn({ targetUserId, roles });
        await loadUsers();
    }
    catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to update user roles';
        errorSignal.set(message);
        console.error('[user-admin.store] Failed to update user roles:', error);
        throw error;
    }
    finally {
        loadingSignal.set(false);
    }
}
/**
 * Clears any stored error message so UIs can dismiss notifications.
 */
export function clearUserAdminError() {
    errorSignal.set('');
}
/**
 * Test-only reset helper to ensure clean state between specs.
 * Not exported publicly to app code.
 */
export function __resetUserAdminStore() {
    usersSignal.set([]);
    loadingSignal.set(false);
    errorSignal.set('');
    functionsInstance = null;
}
//# sourceMappingURL=user-admin.store.js.map